import { LogOutIcon, Loader2, Plus } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useMutation, useQuery } from 'react-query'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'

import { LogoIcon } from '@/assets/icons'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Textarea } from '@/components/ui/textarea'
import { useAppDispatch } from '@/hooks'
import { cn } from '@/lib/utils'
import platformService, {
	PlatformExtra,
	PlatformPackage,
	PlatformPackages,
} from '@/services/platform-service'
import storeService, { AdminStore, StoreStatus, StoreUpsertPayload } from '@/services/store-service'
import { logout } from '@/slices/auth'

const tabs = [
	{ name: 'Stores', value: 'stores' },
	{ name: 'Packages', value: 'packages' },
	{ name: 'Categories', value: 'categories' },
	{ name: 'Types', value: 'types' },
]

const emptyStoreForm = (): StoreUpsertPayload => ({
	name: '',
	slug: '',
	status: 'draft',
	description: '',
	tagline: '',
	address: '',
	city: '',
	zip: '',
	country: 'DE',
	lat: '',
	lng: '',
	phone: '',
	email: '',
	instagram: '',
	paypal_client_id: '',
	paypal_client_secret: '',
	paypal_mode: 'sandbox',
	locafox_tenant_id: '',
	locafox_username: '',
	locafox_password: '',
	locafox_store_id: '1',
	invoice_counter: 0,
	sevdesk_api_key: '',
	geocode: true,
})

export const SuperAdminPanel = () => {
	const dispatch = useAppDispatch()
	const navigate = useNavigate()
	const [searchParams, setSearchParams] = useSearchParams()
	const [selectedTab, setSelectedTab] = useState(searchParams.get('tab') ?? 'stores')
	const [editingUuid, setEditingUuid] = useState<string | null>(null)
	const [form, setForm] = useState<StoreUpsertPayload>(emptyStoreForm())
	const [adminForm, setAdminForm] = useState({
		email: '',
		password: '',
		first_name: '',
		last_name: '',
	})
	const [packages, setPackages] = useState<PlatformPackages>({ packages: [], extras: [] })
	const [nameItem, setNameItem] = useState({ name: '', description: '' })
	const [editingItemId, setEditingItemId] = useState<number | null>(null)

	const storesQuery = useQuery('super-admin-stores', () => storeService.listStores(), {
		enabled: selectedTab === 'stores',
		refetchOnWindowFocus: false,
		onError: () => toast.error('Failed to load stores'),
	})

	const packagesQuery = useQuery('super-admin-packages', () => platformService.getPackages(), {
		enabled: selectedTab === 'packages',
		refetchOnWindowFocus: false,
		onSuccess: data => setPackages(data),
		onError: () => toast.error('Failed to load packages'),
	})

	const categoriesQuery = useQuery('super-admin-categories', () => platformService.listCategories(), {
		enabled: selectedTab === 'categories',
		refetchOnWindowFocus: false,
		onError: () => toast.error('Failed to load categories'),
	})

	const typesQuery = useQuery('super-admin-types', () => platformService.listTypes(), {
		enabled: selectedTab === 'types',
		refetchOnWindowFocus: false,
		onError: () => toast.error('Failed to load types'),
	})

	useEffect(() => {
		sessionStorage.setItem('shouldRedirect', 'false')
	}, [])

	const saveStore = useMutation(
		async () => {
			const payload: StoreUpsertPayload = { ...form }
			if (!payload.lat) delete payload.lat
			if (!payload.lng) delete payload.lng
			if (!payload.paypal_client_secret) delete payload.paypal_client_secret
			if (!payload.locafox_password) delete payload.locafox_password
			if (!payload.sevdesk_api_key) delete payload.sevdesk_api_key
			if (payload.locafox_store_id != null && payload.locafox_store_id !== '') {
				payload.locafox_store_id = Number(payload.locafox_store_id) || 1
			}
			if (editingUuid) return storeService.updateStore(editingUuid, payload)
			return storeService.createStore(payload)
		},
		{
			onSuccess: data => {
				toast.success(editingUuid ? 'Store updated' : 'Draft store created')
				setEditingUuid(data.uuid)
				storesQuery.refetch()
				setForm(prev => ({
					...prev,
					paypal_client_secret: '',
					locafox_password: '',
					sevdesk_api_key: '',
				}))
			},
			onError: (error: any) => {
				toast.error(error?.response?.data?.message ?? 'Save failed')
			},
		},
	)

	const statusMutation = useMutation(
		({ uuid, status }: { uuid: string; status: StoreStatus }) => storeService.setStatus(uuid, status),
		{
			onSuccess: () => {
				toast.success('Status updated')
				storesQuery.refetch()
			},
			onError: (error: any) => {
				toast.error(error?.response?.data?.message ?? 'Status update failed')
			},
		},
	)

	const assignAdminMutation = useMutation(
		() => {
			if (!editingUuid) throw new Error('No store selected')
			return storeService.assignAdmin(editingUuid, adminForm)
		},
		{
			onSuccess: () => {
				toast.success('Store admin assigned')
				setAdminForm({ email: '', password: '', first_name: '', last_name: '' })
				storesQuery.refetch()
			},
			onError: (error: any) => {
				toast.error(error?.response?.data?.message ?? 'Assign failed')
			},
		},
	)

	const savePackages = useMutation(() => platformService.updatePackages(packages), {
		onSuccess: (data: PlatformPackages) => {
			setPackages(data)
			toast.success('Platform packages updated')
		},
		onError: (error: any) => {
			toast.error(error?.response?.data?.message ?? 'Update failed')
		},
	})

	const saveCategory = useMutation(
		async () => {
			if (editingItemId) {
				return platformService.updateCategory(editingItemId, nameItem)
			}
			return platformService.createCategory(nameItem)
		},
		{
			onSuccess: () => {
				toast.success(editingItemId ? 'Category updated' : 'Category created')
				setNameItem({ name: '', description: '' })
				setEditingItemId(null)
				categoriesQuery.refetch()
			},
			onError: (error: any) => {
				toast.error(error?.response?.data?.message ?? 'Save failed')
			},
		},
	)

	const deleteCategory = useMutation((id: number) => platformService.deleteCategory(id), {
		onSuccess: () => {
			toast.success('Category deleted')
			categoriesQuery.refetch()
		},
		onError: (error: any) => {
			toast.error(error?.response?.data?.message ?? 'Delete failed')
		},
	})

	const saveType = useMutation(
		async () => {
			if (editingItemId) return platformService.updateType(editingItemId, nameItem)
			return platformService.createType(nameItem)
		},
		{
			onSuccess: () => {
				toast.success(editingItemId ? 'Type updated' : 'Type created')
				setNameItem({ name: '', description: '' })
				setEditingItemId(null)
				typesQuery.refetch()
			},
			onError: (error: any) => {
				toast.error(error?.response?.data?.message ?? 'Save failed')
			},
		},
	)

	const deleteType = useMutation((id: number) => platformService.deleteType(id), {
		onSuccess: () => {
			toast.success('Type deleted')
			typesQuery.refetch()
		},
		onError: (error: any) => {
			toast.error(error?.response?.data?.message ?? 'Delete failed')
		},
	})

	const openCreate = () => {
		setEditingUuid(null)
		setForm(emptyStoreForm())
	}

	const openEdit = (store: AdminStore) => {
		setEditingUuid(store.uuid)
		setForm({
			name: store.name || '',
			slug: store.slug || '',
			status: store.status,
			description: store.description || '',
			tagline: store.tagline || '',
			address: store.address || '',
			city: store.city || '',
			zip: store.zip || '',
			country: store.country || 'DE',
			lat: store.lat ?? '',
			lng: store.lng ?? '',
			phone: store.phone || '',
			email: store.email || '',
			instagram: store.instagram || '',
			paypal_client_id: store.paypal_client_id || '',
			paypal_client_secret: '',
			paypal_mode: store.paypal_mode || 'sandbox',
			locafox_tenant_id: store.locafox_tenant_id || '',
			locafox_username: store.locafox_username || '',
			locafox_password: '',
			locafox_store_id: String(store.locafox_store_id ?? 1),
			invoice_counter: store.invoice_counter ?? 0,
			sevdesk_api_key: '',
			geocode: true,
		})
	}

	const setField = (key: keyof StoreUpsertPayload, value: string | boolean) => {
		setForm(prev => ({ ...prev, [key]: value }))
	}

	const updatePackageRow = (index: number, key: keyof PlatformPackage, value: string) => {
		setPackages(prev => {
			const next = [...prev.packages]
			const row = { ...next[index] }
			if (key === 'title') row.title = value
			else row[key] = Number(value) as never
			next[index] = row
			return { ...prev, packages: next }
		})
	}

	const updateExtraRow = (index: number, key: keyof PlatformExtra, value: string) => {
		setPackages(prev => {
			const next = [...prev.extras]
			const row = { ...next[index] }
			if (key === 'title') row.title = value
			else row.price = Number(value)
			next[index] = row
			return { ...prev, extras: next }
		})
	}

	const onTabChange = (value: string) => {
		setSelectedTab(value)
		setSearchParams({ tab: value })
		setEditingItemId(null)
		setNameItem({ name: '', description: '' })
	}

	return (
		<div className="p-3 sm:p-5 max-w-[90rem] mx-auto bg-[#F6F6F680] mb-0 flex flex-col gap-4 min-w-0 overflow-x-clip">
			<div className="rounded-[20px] bg-white p-3 sm:p-4 lg:p-6 flex flex-col gap-4">
				<div className="flex items-center justify-between gap-3">
					<div className="flex items-center gap-2 sm:gap-3 min-w-0">
						<LogoIcon
							onClick={() => navigate('/')}
							className="size-10 sm:size-12 cursor-pointer shrink-0"
						/>
						<div className="min-w-0">
							<h1 className="text-secondary font-extrabold text-base sm:text-lg">Super Admin</h1>
							<p className="text-muted-foreground text-xs sm:text-sm truncate">
								Franchises, packages, categories &amp; types
							</p>
						</div>
					</div>
					<button
						type="button"
						onClick={() => dispatch(logout())}
						className="border cursor-pointer border-[#EEEFF0] size-9 sm:size-10 flex items-center justify-center rounded-xl shrink-0">
						<LogOutIcon className="size-5 shrink-0 rotate-180" />
					</button>
				</div>
				<select
					className="lg:hidden border border-[#EEEFF0] rounded-xl h-11 px-3 text-sm text-secondary font-medium bg-[#FBFBFB] w-full"
					value={selectedTab}
					onChange={e => onTabChange(e.target.value)}
					aria-label="Bereich wählen">
					{tabs.map(tab => (
						<option key={tab.value} value={tab.value}>
							{tab.name}
						</option>
					))}
				</select>
				<Tabs value={selectedTab} onValueChange={onTabChange} className="max-lg:hidden">
					<TabsList className="p-1 bg-[#FBFBFB] h-12 overflow-x-auto flex w-full items-stretch justify-start gap-2">
						{tabs.map(tab => (
							<TabsTrigger
								key={tab.value}
								value={tab.value}
								className="py-2 px-5 data-[state=active]:text-white text-muted-foreground text-sm data-[state=active]:bg-secondary rounded-xl shrink-0">
								{tab.name}
							</TabsTrigger>
						))}
					</TabsList>
				</Tabs>
			</div>

			{selectedTab === 'stores' && (
				<div className="grid lg:grid-cols-[1fr_1.2fr] gap-4 min-w-0">
					<div className="rounded-[20px] bg-white p-3 sm:p-4 lg:p-6 flex flex-col gap-3 min-w-0">
						<div className="flex items-center justify-between gap-2">
							<h2 className="text-secondary font-bold">All stores</h2>
							<Button type="button" size="sm" onClick={openCreate} className="shrink-0">
								<Plus className="size-4" /> New draft
							</Button>
						</div>
						{storesQuery.isLoading ? (
							<div className="flex justify-center py-10">
								<Loader2 className="animate-spin" />
							</div>
						) : (
							<div className="flex flex-col gap-2">
								{(storesQuery.data || []).map(store => (
									<button
										key={store.uuid}
										type="button"
										onClick={() => openEdit(store)}
										className={cn(
											'text-left rounded-xl border px-4 py-3 transition',
											editingUuid === store.uuid
												? 'border-secondary bg-secondary/5'
												: 'border-[#EEEFF0] hover:border-secondary/40',
										)}>
										<div className="flex items-center justify-between gap-2">
											<div>
												<p className="font-semibold text-secondary">{store.name}</p>
												<p className="text-xs text-muted-foreground">
													{store.city || '—'} · {store.slug}
												</p>
											</div>
											<span
												className={cn(
													'text-xs font-semibold px-2 py-1 rounded-md capitalize',
													store.status === 'active' && 'bg-emerald-100 text-emerald-800',
													store.status === 'draft' && 'bg-amber-100 text-amber-800',
													store.status === 'suspended' && 'bg-rose-100 text-rose-800',
												)}>
												{store.status}
											</span>
										</div>
										{store.onboarding && !store.onboarding.complete && (
											<p className="mt-1 text-xs text-muted-foreground">
												Missing: {store.onboarding.missing.join(', ')}
											</p>
										)}
									</button>
								))}
							</div>
						)}
					</div>

					<div className="rounded-[20px] bg-white p-3 sm:p-4 lg:p-6 flex flex-col gap-4 min-w-0">
						<div className="flex items-center justify-between gap-2 flex-wrap">
							<h2 className="text-secondary font-bold text-base sm:text-lg">
								{editingUuid ? 'Edit store' : 'Create draft store'}
							</h2>
							{editingUuid && (
								<div className="flex gap-2">
									{(['draft', 'active', 'suspended'] as StoreStatus[]).map(status => (
										<Button
											key={status}
											type="button"
											size="sm"
											variant={form.status === status ? 'default' : 'outline'}
											disabled={statusMutation.isLoading}
											onClick={() => statusMutation.mutate({ uuid: editingUuid, status })}>
											{status}
										</Button>
									))}
								</div>
							)}
						</div>

						<div className="grid sm:grid-cols-2 gap-3">
							<Field label="Name" value={form.name} onChange={v => setField('name', v)} />
							<Field label="Slug" value={form.slug || ''} onChange={v => setField('slug', v)} />
							<Field label="Address" value={form.address || ''} onChange={v => setField('address', v)} />
							<Field label="City" value={form.city || ''} onChange={v => setField('city', v)} />
							<Field label="ZIP" value={form.zip || ''} onChange={v => setField('zip', v)} />
							<Field
								label="Country"
								value={form.country || 'DE'}
								onChange={v => setField('country', v)}
							/>
							<Field
								label="Lat (optional override)"
								value={String(form.lat ?? '')}
								onChange={v => setField('lat', v)}
							/>
							<Field
								label="Lng (optional override)"
								value={String(form.lng ?? '')}
								onChange={v => setField('lng', v)}
							/>
							<Field label="Phone" value={form.phone || ''} onChange={v => setField('phone', v)} />
							<Field label="Email" value={form.email || ''} onChange={v => setField('email', v)} />
							<Field
								label="Instagram"
								value={form.instagram || ''}
								onChange={v => setField('instagram', v)}
							/>
							<Field
								label="Tagline"
								value={form.tagline || ''}
								onChange={v => setField('tagline', v)}
							/>
						</div>
						<div>
							<Label className="text-xs">Description</Label>
							<Textarea
								className="mt-1"
								value={form.description || ''}
								onChange={e => setField('description', e.target.value)}
							/>
						</div>

						<div className="border-t pt-4 grid sm:grid-cols-2 gap-3">
							<p className="sm:col-span-2 text-sm font-semibold text-secondary">
								Secrets (write-only — leave blank to keep existing)
							</p>
							<Field
								label="PayPal client id"
								value={form.paypal_client_id || ''}
								onChange={v => setField('paypal_client_id', v)}
							/>
							<Field
								label="PayPal mode"
								value={form.paypal_mode || 'sandbox'}
								onChange={v => setField('paypal_mode', v)}
							/>
							<Field
								label="PayPal client secret"
								value={form.paypal_client_secret || ''}
								onChange={v => setField('paypal_client_secret', v)}
								type="password"
							/>
							<Field
								label="Locafox tenant id"
								value={form.locafox_tenant_id || ''}
								onChange={v => setField('locafox_tenant_id', v)}
							/>
							<Field
								label="Locafox username"
								value={form.locafox_username || ''}
								onChange={v => setField('locafox_username', v)}
							/>
							<Field
								label="Locafox password"
								value={form.locafox_password || ''}
								onChange={v => setField('locafox_password', v)}
								type="password"
							/>
							<Field
								label="Locafox store id (location)"
								value={String(form.locafox_store_id ?? '1')}
								onChange={v => setField('locafox_store_id', v)}
							/>
							<Field
								label="Invoice counter"
								value={String(form.invoice_counter ?? 0)}
								onChange={v => setField('invoice_counter', v)}
							/>
							<p className="sm:col-span-2 text-xs text-muted-foreground">
								SevDesk URL is shared for all stores:{' '}
								<span className="font-medium text-secondary">
									https://my.sevdesk.de/api/v1
								</span>
							</p>
							<Field
								label="SevDesk API key"
								value={form.sevdesk_api_key || ''}
								onChange={v => setField('sevdesk_api_key', v)}
								type="password"
							/>
						</div>

						<Button
							type="button"
							disabled={saveStore.isLoading || !form.name}
							onClick={() => saveStore.mutate()}>
							{saveStore.isLoading && <Loader2 className="animate-spin" />}
							{editingUuid ? 'Save store' : 'Create draft'}
						</Button>

						{editingUuid && (
							<div className="border-t pt-4 flex flex-col gap-3">
								<p className="text-sm font-semibold text-secondary">Assign store admin</p>
								<div className="grid sm:grid-cols-2 gap-3">
									<Field
										label="Email"
										value={adminForm.email}
										onChange={v => setAdminForm(p => ({ ...p, email: v }))}
									/>
									<Field
										label="Password (new users only)"
										value={adminForm.password}
										onChange={v => setAdminForm(p => ({ ...p, password: v }))}
										type="password"
									/>
									<Field
										label="First name"
										value={adminForm.first_name}
										onChange={v => setAdminForm(p => ({ ...p, first_name: v }))}
									/>
									<Field
										label="Last name"
										value={adminForm.last_name}
										onChange={v => setAdminForm(p => ({ ...p, last_name: v }))}
									/>
								</div>
								<Button
									type="button"
									variant="outline"
									disabled={assignAdminMutation.isLoading || !adminForm.email}
									onClick={() => assignAdminMutation.mutate()}>
									{assignAdminMutation.isLoading && <Loader2 className="animate-spin" />}
									Assign admin
								</Button>
								{(storesQuery.data?.find(s => s.uuid === editingUuid)?.admins || []).map(admin => (
									<p key={admin.user_id} className="text-xs text-muted-foreground">
										Admin: {admin.email} ({admin.first_name} {admin.last_name})
									</p>
								))}
							</div>
						)}
					</div>
				</div>
			)}

			{selectedTab === 'packages' && (
				<div className="rounded-[20px] bg-white p-4 lg:p-6 flex flex-col gap-4">
					<div className="flex items-center justify-between">
						<h2 className="text-secondary font-bold">Platform packages &amp; extras</h2>
						<Button
							type="button"
							disabled={savePackages.isLoading || packagesQuery.isLoading}
							onClick={() => savePackages.mutate()}>
							{savePackages.isLoading && <Loader2 className="animate-spin" />}
							Save packages
						</Button>
					</div>
					<div className="overflow-x-auto">
						<table className="w-full text-sm">
							<thead>
								<tr className="text-left text-muted-foreground">
									<th className="py-2">Title</th>
									<th>Price</th>
									<th>Provision %</th>
									<th>Duration (days)</th>
								</tr>
							</thead>
							<tbody>
								{packages.packages.map((row, index) => (
									<tr key={row.id} className="border-t">
										<td className="py-2 pr-2">
											<Input
												value={row.title}
												onChange={e => updatePackageRow(index, 'title', e.target.value)}
											/>
										</td>
										<td className="pr-2">
											<Input
												type="number"
												value={row.price}
												onChange={e => updatePackageRow(index, 'price', e.target.value)}
											/>
										</td>
										<td className="pr-2">
											<Input
												type="number"
												value={row.provision}
												onChange={e => updatePackageRow(index, 'provision', e.target.value)}
											/>
										</td>
										<td>
											<Input
												type="number"
												value={row.duration}
												onChange={e => updatePackageRow(index, 'duration', e.target.value)}
											/>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
					<div className="overflow-x-auto">
						<p className="font-semibold text-secondary mb-2">Extras</p>
						<table className="w-full text-sm">
							<thead>
								<tr className="text-left text-muted-foreground">
									<th className="py-2">Title</th>
									<th>Price</th>
								</tr>
							</thead>
							<tbody>
								{packages.extras.map((row, index) => (
									<tr key={row.id} className="border-t">
										<td className="py-2 pr-2">
											<Input
												value={row.title}
												onChange={e => updateExtraRow(index, 'title', e.target.value)}
											/>
										</td>
										<td>
											<Input
												type="number"
												value={row.price}
												onChange={e => updateExtraRow(index, 'price', e.target.value)}
											/>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				</div>
			)}

			{(selectedTab === 'categories' || selectedTab === 'types') && (
				<div className="rounded-[20px] bg-white p-4 lg:p-6 flex flex-col gap-4">
					<h2 className="text-secondary font-bold capitalize">Platform {selectedTab}</h2>
					<div className="grid sm:grid-cols-[1fr_1fr_auto] gap-3 items-end">
						<div>
							<Label className="text-xs">Name</Label>
							<Input
								className="mt-1"
								value={nameItem.name}
								onChange={e => setNameItem(p => ({ ...p, name: e.target.value }))}
							/>
						</div>
						<div>
							<Label className="text-xs">Description</Label>
							<Input
								className="mt-1"
								value={nameItem.description}
								onChange={e => setNameItem(p => ({ ...p, description: e.target.value }))}
							/>
						</div>
						<Button
							type="button"
							disabled={
								!nameItem.name ||
								(selectedTab === 'categories' ? saveCategory.isLoading : saveType.isLoading)
							}
							onClick={() =>
								selectedTab === 'categories' ? saveCategory.mutate() : saveType.mutate()
							}>
							{editingItemId ? 'Update' : 'Create'}
						</Button>
					</div>
					<div className="flex flex-col gap-2">
						{(selectedTab === 'categories'
							? categoriesQuery.data || []
							: typesQuery.data || []
						).map(item => (
							<div
								key={item.id}
								className="flex items-center justify-between gap-3 border border-[#EEEFF0] rounded-xl px-4 py-3">
								<div>
									<p className="font-medium text-secondary">{item.name}</p>
									{item.description && (
										<p className="text-xs text-muted-foreground">{item.description}</p>
									)}
								</div>
								<div className="flex gap-2">
									<Button
										type="button"
										size="sm"
										variant="outline"
										onClick={() => {
											setEditingItemId(item.id)
											setNameItem({
												name: item.name,
												description: item.description || '',
											})
										}}>
										Edit
									</Button>
									<Button
										type="button"
										size="sm"
										variant="destructive"
										onClick={() =>
											selectedTab === 'categories'
												? deleteCategory.mutate(item.id)
												: deleteType.mutate(item.id)
										}>
										Delete
									</Button>
								</div>
							</div>
						))}
					</div>
				</div>
			)}
		</div>
	)
}

const Field = ({
	label,
	value,
	onChange,
	type = 'text',
}: {
	label: string
	value: string
	onChange: (v: string) => void
	type?: string
}) => (
	<div>
		<Label className="text-xs">{label}</Label>
		<Input className="mt-1" type={type} value={value} onChange={e => onChange(e.target.value)} />
	</div>
)
